/**
 * Shared scroll-to-reveal math for the option pickers (`SearchableSelect`,
 * `ListPicker`).
 *
 * Both render a bounded, scrollable dropdown over a list that can be
 * arbitrarily long. Rather than truncating — which silently hides options
 * and makes the dropdown look complete when it isn't — they render a first
 * chunk and reveal another each time a sentinel at the bottom of the scroll
 * region comes into view.
 */

/**
 * Rows revealed per scroll step, and the default size of the first chunk.
 * MUST be large enough that the first chunk overflows the dropdown's height
 * cap (24rem = 384px at ~37px/row) whenever more rows exist — an overflowing
 * region is what makes the scrollbar appear and scroll/observer events fire
 * at all. A test pins this invariant.
 */
export const REVEAL_CHUNK = 25

/** Scroll-listener backstop: reveal when within this many px of the bottom. */
export const REVEAL_THRESHOLD_PX = 48

/** IntersectionObserver prefetch margin around the sentinel. */
export const REVEAL_ROOT_MARGIN = '96px'

/**
 * Pure predicate for the scroll-listener backstop: true when the region is
 * scrolled to within `thresholdPx` of its bottom and more rows exist.
 */
export function shouldRevealMore(
  metrics: { scrollHeight: number; scrollTop: number; clientHeight: number },
  hasMore: boolean,
  thresholdPx: number = REVEAL_THRESHOLD_PX,
): boolean {
  return hasMore && metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight <= thresholdPx
}

/**
 * Next reveal step. Clamped to `total` so the IntersectionObserver driving
 * it converges instead of incrementing forever, and monotonic so a late
 * callback can never shrink the rendered list.
 *
 * Pure so it can be unit-tested without a DOM.
 */
export function nextVisibleCount(current: number, total: number, chunk: number = REVEAL_CHUNK): number {
  if (current >= total) {
    return total
  }
  return Math.min(current + Math.max(chunk, 1), total)
}
